import { useEffect } from 'react'
import { useQueryClient } from 'react-query'

import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { scheduleKeys } from 'hooks/queries/schedules/scheduleKeys'
import { getUniqueIdentifier } from 'utils/sessionStorage'

export type WorkspaceCalendarPayload =
  | {
      type: 'almanac_file_changed'
      almanac_file_key: string | null
      schedule_id: number | null
      session_uuid?: string | null
    }
  | {
      type: 'batch_changed'
      almanac_file_key: string | null
      schedule_id: number | null
      session_uuid?: string | null
    }

export const useWorkspaceCalendarChannel = (enabled: boolean = true) => {
  const websocketConnection = useWebsocketConnection()
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const currentSessionUuid = getUniqueIdentifier()

  useEffect(() => {
    if (!enabled) return
    if (!websocketConnection || !workspaceId) return

    const subscription = websocketConnection.subscriptions.create(
      { channel: 'WorkspaceCalendarChannel', workspace_id: workspaceId },
      {
        received(payload: WorkspaceCalendarPayload) {
          const isSameSession = !!payload.session_uuid && payload.session_uuid === currentSessionUuid

          if (payload.type === 'almanac_file_changed') {
            if (!isSameSession) {
              queryClient.invalidateQueries(filesKeys.partialCalendar(workspaceId))
            }
            if (payload.schedule_id) {
              queryClient.invalidateQueries(scheduleKeys.activeBatch(payload.schedule_id))
              queryClient.invalidateQueries(scheduleKeys.detail(payload.schedule_id))
            }
            return
          }
          if (payload.type === 'batch_changed') {
            if (payload.schedule_id) {
              queryClient.setQueryData(scheduleKeys.activeBatchId(workspaceId), payload.schedule_id)
              queryClient.invalidateQueries(scheduleKeys.activeBatch(payload.schedule_id))
              queryClient.invalidateQueries(scheduleKeys.detail(payload.schedule_id))
            }
            queryClient.invalidateQueries(scheduleKeys.pending(workspaceId))
            if (!isSameSession) {
              queryClient.invalidateQueries(filesKeys.partialCalendar(workspaceId))
            }
          }
        },
      },
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [enabled, websocketConnection, workspaceId, queryClient, currentSessionUuid])
}
